import React, { useState } from 'react';
import { useRunPlan } from '../context/RunPlanContext';
import { useSettings } from '../context/SettingsContext';
import { REST_DAY_ICONS } from '../constants';
import ThemePickerModal from './ThemePickerModal';
import { formatDateRange } from '../utils/formatters';

const SettingsView = () => {
  const {
    selectedPlan,
    darkMode,
    setDarkMode,
    weekStartsMonday,
    setWeekStartsMonday,
    verticalLayout,
    setVerticalLayout,
    abbreviatedWeekView,
    setAbbreviatedWeekView,
    showRunEmojis,
    setShowRunEmojis,
    t: theme,
    theme: themeName,
    setTheme,
    themes,
    restDayIcon,
    setRestDayIcon,
    exportCSV,
    importCSV,
    sharePlan,
    savedPlans,
    saveCurrentPlanToLibrary,
    loadSavedPlan,
    deleteSavedPlan,
    hiddenWeeks,
    unhideWeek,
    weeks
  } = useRunPlan();
  const { scoutEnabled, setScoutEnabled, autoHideEmptyWeeks, setAutoHideEmptyWeeks } = useSettings();
  const [showRestDayOptions, setShowRestDayOptions] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);

  // Theme display names
  const themeNames = {
    blue: 'Blue Sky',
    meadow: 'Meadow',
    amber: 'Amber',
    forest: 'Forest',
    hazelnut: 'Hazelnut',
    sunset: 'Sunset',
    ocean: 'Ocean'
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to clear all data? This cannot be undone.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  // Wrapper functions with GA tracking
  const handleDarkModeToggle = (checked) => {
    setDarkMode(checked);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'toggle_dark_mode', {
        enabled: checked
      });
    }
  };

  const handleWeekStartToggle = (checked) => {
    setWeekStartsMonday(checked);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'toggle_week_start', {
        starts_monday: checked
      });
    }
  };

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'change_theme', {
        theme: newTheme
      });
    }
  };

  return (
    <div className="p-4 pb-20">
      <h2 className="text-2xl font-semibold mb-6">Settings</h2>
      <div className="space-y-6">

        {/* Appearance Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-3 text-slate-300' : 'text-lg font-semibold mb-3 text-slate-700'}>Appearance</h3>
          <div className="space-y-4">
            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <span className="font-medium">Dark Mode</span>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={darkMode} onChange={(e) => handleDarkModeToggle(e.target.checked)} className="sr-only" />
                <div className={darkMode ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={darkMode ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>

            <div className={darkMode ? 'py-4 border-b border-slate-700' : 'py-4 border-b border-slate-200'}>
              <div className="flex justify-between items-center">
                <span className="font-medium">Theme</span>
                <button
                  onClick={() => setShowThemePicker(true)}
                  className={darkMode ? 'flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-800 border border-slate-700 hover:border-slate-600 transition-colors' : 'flex items-center gap-3 px-4 py-2 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition-colors'}
                >
                  <div className={`w-8 h-8 rounded-xl bg-gradient-to-br ${theme.grad} shadow-sm`} />
                  <span className="font-medium">{themeNames[themeName] || themeName}</span>
                  <span className={darkMode ? 'text-slate-500' : 'text-slate-400'}>›</span>
                </button>
              </div>
            </div>

            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <span className="font-medium">Show Run Type Emojis</span>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={showRunEmojis} onChange={(e) => setShowRunEmojis(e.target.checked)} className="sr-only" />
                <div className={showRunEmojis ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={showRunEmojis ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>

            <div className={darkMode ? 'py-4 border-b border-slate-700' : 'py-4 border-b border-slate-200'}>
              <div className="flex justify-between items-center">
                <span className="font-medium">Rest Day Icon</span>
                <button
                  onClick={() => setShowRestDayOptions(!showRestDayOptions)}
                  className={darkMode ? 'flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-800 border border-slate-700' : 'flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200'}
                >
                  <span className="text-2xl">{REST_DAY_ICONS[restDayIcon].icon}</span>
                  <span className="text-sm">{REST_DAY_ICONS[restDayIcon].name}</span>
                </button>
              </div>
              {showRestDayOptions && (
                <div className="grid grid-cols-6 gap-2 mt-3">
                  {Object.entries(REST_DAY_ICONS).map(([key, config]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setRestDayIcon(key);
                        setShowRestDayOptions(false);
                      }}
                      className={
                        darkMode
                          ? `p-2 rounded-xl border-2 transition-all ${
                              restDayIcon === key
                                ? config.borderDark + ' ' + config.bgDark
                                : 'border-slate-700 bg-slate-800 opacity-70'
                            }`
                          : `p-2 rounded-xl border-2 transition-all ${
                              restDayIcon === key
                                ? config.borderLight + ' ' + config.bgLight
                                : 'border-slate-200 bg-slate-50 opacity-70'
                            }`
                      }
                    >
                      <div className="text-2xl text-center">{config.icon}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Calendar View Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-3 text-slate-300' : 'text-lg font-semibold mb-3 text-slate-700'}>Calendar View</h3>
          <div className="space-y-4">
            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <span className="font-medium">Week Starts on Monday</span>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={weekStartsMonday} onChange={(e) => handleWeekStartToggle(e.target.checked)} className="sr-only" />
                <div className={weekStartsMonday ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={weekStartsMonday ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>

            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <span className="font-medium">Vertical Layout</span>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={verticalLayout} onChange={(e) => setVerticalLayout(e.target.checked)} className="sr-only" />
                <div className={verticalLayout ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={verticalLayout ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>

            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <div>
                <div className="font-medium">Abbreviated Week View</div>
                <div className={darkMode ? 'text-sm text-slate-400 mt-1' : 'text-sm text-slate-500 mt-1'}>
                  Use "M T W Th F S Su" and hide day numbers
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={abbreviatedWeekView} onChange={(e) => setAbbreviatedWeekView(e.target.checked)} className="sr-only" />
                <div className={abbreviatedWeekView ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={abbreviatedWeekView ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Week Visibility Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-3 text-slate-300' : 'text-lg font-semibold mb-3 text-slate-700'}>Week Visibility</h3>
          <div className="space-y-4">
            <div className={darkMode ? 'flex justify-between py-4' + (hiddenWeeks.length === 0 ? ' border-b border-slate-700' : '') : 'flex justify-between py-4' + (hiddenWeeks.length === 0 ? ' border-b border-slate-200' : '')}>
              <div>
                <div className="font-medium">Auto-hide Empty Weeks</div>
                <div className={darkMode ? 'text-sm text-slate-400 mt-1' : 'text-sm text-slate-500 mt-1'}>
                  Automatically hide weeks with no runs from calendar view
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={autoHideEmptyWeeks} onChange={(e) => setAutoHideEmptyWeeks(e.target.checked)} className="sr-only" />
                <div className={autoHideEmptyWeeks ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={autoHideEmptyWeeks ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>

            {/* Hidden Weeks Summary */}
            {hiddenWeeks.length > 0 && (
              <div className={darkMode ? 'flex justify-between py-4 border-t border-b border-slate-700' : 'flex justify-between py-4 border-t border-b border-slate-200'}>
                <div>
                  <div className="font-medium">{hiddenWeeks.length} Hidden Week{hiddenWeeks.length !== 1 ? 's' : ''}</div>
                  <div className={darkMode ? 'text-sm text-slate-400 mt-1' : 'text-sm text-slate-500 mt-1'}>
                    Manually hidden weeks are not displayed in calendar
                  </div>
                </div>
                <button
                  onClick={() => {
                    hiddenWeeks.forEach(wi => unhideWeek(wi));
                  }}
                  className={'px-3 py-2 rounded-xl text-sm font-medium transition-colors ' + theme.primary + ' text-white ' + theme.hover}
                >
                  Unhide All
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-3 text-slate-300' : 'text-lg font-semibold mb-3 text-slate-700'}>Features</h3>
          <div className="space-y-4">
            <div className={darkMode ? 'flex justify-between py-4 border-b border-slate-700' : 'flex justify-between py-4 border-b border-slate-200'}>
              <div>
                <div className="font-medium">Scout Game</div>
                <div className={darkMode ? 'text-sm text-slate-400 mt-1' : 'text-sm text-slate-500 mt-1'}>
                  Grow a virtual forest by collecting and growing seeds with your runs
                </div>
              </div>
              <label className="relative inline-flex cursor-pointer">
                <input type="checkbox" checked={scoutEnabled} onChange={(e) => setScoutEnabled(e.target.checked)} className="sr-only" />
                <div className={scoutEnabled ? `w-11 h-6 rounded-full ${theme.primary}` : 'w-11 h-6 rounded-full bg-slate-300'}>
                  <div className={scoutEnabled ? 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-6 transition-all' : 'w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-all'} />
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Saved Plans Library */}
        {savedPlans.length > 0 && (
          <div>
            <h3 className={darkMode ? 'text-lg font-semibold mb-3 text-slate-300' : 'text-lg font-semibold mb-3 text-slate-700'}>Saved Plans</h3>
            <div className="space-y-3">
              {savedPlans.map((plan) => (
                <div
                  key={plan.id}
                  className={darkMode ? 'p-4 rounded-xl border bg-slate-800 border-slate-700' : 'p-4 rounded-xl border bg-slate-50 border-slate-200'}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold">{plan.name}</h4>
                      <p className={darkMode ? 'text-xs text-slate-400 mt-1' : 'text-xs text-slate-500 mt-1'}>
                        Saved {new Date(plan.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={darkMode ? 'text-xs px-2 py-1 rounded bg-slate-700 text-slate-300' : 'text-xs px-2 py-1 rounded bg-slate-200 text-slate-600'}>
                      {Object.keys(plan.runs || {}).length} runs
                    </span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => loadSavedPlan(plan.id)}
                      className={'flex-1 py-2 text-sm rounded-xl text-white font-medium ' + theme.primary + ' ' + theme.hover}
                    >
                      Load
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${plan.name}"? This cannot be undone.`)) {
                          deleteSavedPlan(plan.id);
                        }
                      }}
                      className={darkMode ? 'px-4 py-2 text-sm rounded-xl bg-slate-700 hover:bg-rose-600 text-slate-300 hover:text-white' : 'px-4 py-2 text-sm rounded-xl bg-slate-200 hover:bg-rose-500 text-slate-600 hover:text-white'}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Share & Save Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-4 text-slate-300' : 'text-lg font-semibold mb-4 text-slate-700'}>Share & Save</h3>
          <div className="space-y-3">
            <button
              onClick={sharePlan}
              className={'w-full text-white py-3 rounded-xl font-medium transition-colors ' + theme.primary + ' ' + theme.hover}
            >
              🔗 Share Plan via Link
            </button>

            {selectedPlan && (
              <button
                onClick={() => {
                  if (saveCurrentPlanToLibrary()) {
                    alert('Current plan saved to library!');
                  } else {
                    alert('No plan to save. Please add some runs first.');
                  }
                }}
                className={darkMode ? 'w-full py-3 rounded-xl bg-slate-700 hover:bg-slate-600 font-medium transition-colors' : 'w-full py-3 rounded-xl bg-slate-200 hover:bg-slate-300 font-medium transition-colors'}
              >
                💾 Save Current Plan to Library
              </button>
            )}
          </div>
        </div>

        {/* Import/Export Section */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-4 text-slate-300' : 'text-lg font-semibold mb-4 text-slate-700'}>Import/Export</h3>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={exportCSV}
              className={'text-white py-3 rounded-xl font-medium transition-colors ' + theme.primary + ' ' + theme.hover}
            >
              Export CSV
            </button>
            <label className={darkMode ? 'text-center py-3 rounded-xl cursor-pointer bg-slate-700 hover:bg-slate-600 font-medium transition-colors' : 'text-center py-3 rounded-xl cursor-pointer bg-slate-200 hover:bg-slate-300 font-medium transition-colors'}>
              Import CSV
              <input type="file" accept=".csv" onChange={importCSV} className="hidden" />
            </label>
          </div>
        </div>

        {/* Danger Zone */}
        <div>
          <h3 className={darkMode ? 'text-lg font-semibold mb-4 text-slate-300' : 'text-lg font-semibold mb-4 text-slate-700'}>Danger Zone</h3>
          <button
            onClick={clearAllData}
            className={darkMode ? 'w-full py-3 rounded-xl bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 font-medium transition-colors border border-rose-500/20' : 'w-full py-3 rounded-xl bg-rose-50 text-rose-600 hover:bg-rose-100 font-medium transition-colors border border-rose-200'}
          >
            ⚠️ Clear All Data
          </button>
        </div>

      </div>

      {/* Theme Picker Modal */}
      {showThemePicker && (
        <ThemePickerModal
          darkMode={darkMode}
          themes={themes}
          currentTheme={themeName}
          onSelectTheme={handleThemeChange}
          onClose={() => setShowThemePicker(false)}
        />
      )}
    </div>
  );
};

export default SettingsView;
